<template>
  <div class="company-alarms-summary" :style="summaryStyle">
    <span>
      <i
        aria-hidden="true"
        :class="iconClass"
      />
      <span v-html="summaryText"></span>
    </span>
    <info-badge
      v-if="isAllInstallationsBlocked || companyIsBlocked"
      translation-key="alarms-and-warnings.blocked-company-tooltip"
    />
  </div>
</template>

<script>
import InfoBadge from '@/components/icon/InfoBadge';

export default {
  components: {
    InfoBadge,
  },
  props: {
    company: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  computed: {
    isAllInstallationsBlocked() {
      return (this.company.summary || {}).allInstallationsBlocked;
    },
    companyIsBlocked() {
      return (this.company.summary || {}).companyBlocked;
    },
    isAllInstallationsDisabled() {
      return (this.company.summary || {}).allInstallationsDisabled;
    },
    iconClass() {
      const icons = {
        'var(--color-background-dark)': 'fas fa-ban',
        'var(--color-danger)': 'fas fa-exclamation-triangle',
        'var(--color-info-light)': 'fas fa-minus-circle',
        'var(--color-light-dark)': 'fas fa-times-circle',
        'var(--color-success-light)': 'fas fa-check',
      };
      return `${icons[this.backgroundColorByRules]} mr-2 mb-1`;
    },
    summaryStyle() {
      return {
        'background-color': this.backgroundColorByRules,
        color: this.fontColor,
      };
    },
    fontColor() {
      const textColorByBackground = {
        'var(--color-background-dark)': 'var(--color-gray-intermediary)',
        'var(--color-danger)': 'var(--color-white-100)',
        'var(--color-info-light)': 'var(--color-black-87)',
        'var(--color-light-dark)': 'var(--color-white-100)',
        'var(--color-success-light)': 'var(--color-black-87)',
      };
      return textColorByBackground[this.backgroundColorByRules];
    },
    backgroundColorByRules() {
      const defaultColor = 'var(--color-success-light)';
      const { summary } = this.company;
      if (!summary) return defaultColor;
      const colorRules = {
        'var(--color-background-dark)': this.isAllInstallationsDisabled || this.isAllInstallationsBlocked || this.companyIsBlocked,
        'var(--color-danger)': (summary.alarms || {}).actedAndNotRecognized,
        'var(--color-info-light)': (summary.alarms || {}).actedAndRecognized,
        'var(--color-light-dark)': (summary.alarms || {}).notActedAndNotRecognized,
      };
      const colors = Object.keys(colorRules);
      const attendedRule = colors.find(color => !!colorRules[color]);
      return attendedRule || defaultColor;
    },
    summaryText() {
      const color = this.backgroundColorByRules;
      const textByColors = {
        'var(--color-background-dark)': this.lightGreyText,
        'var(--color-danger)': this.redText,
        'var(--color-info-light)': this.blueText,
        'var(--color-light-dark)': this.lightBlackText,
        'var(--color-success-light)': this.lightGreenText,
      };
      return textByColors[color];
    },
    lightGreyText() {
      if (this.isAllInstallationsDisabled) {
        return this.$t('alarms-and-warnings.disabled-company');
      }
      return this.$t('alarms-and-warnings.blocked-company');
    },
    redText() {
      const summary = this.company.summary || {};
      const {
        actedAndNotRecognized,
        actedAndRecognized,
        notActedAndNotRecognized,
      } = summary.alarms || {};

      const allActedAlarms = actedAndRecognized + actedAndNotRecognized;
      const allAlarmsNotRecognized = notActedAndNotRecognized + actedAndNotRecognized;
      if (this.hasActedNotRecognizedAndNotActedNotRecognizedAlarms(
        actedAndNotRecognized,
        notActedAndNotRecognized,
      )) {
        return this.$t('alarms-and-warnings.acted-not-recognized-not-acted-not-recognized', {
          allActedAlarms,
          notActedAndNotRecognized,
          allAlarmsNotRecognized,
        });
      }
      return this.$t('alarms-and-warnings.acted-not-recognized', {
        allActedAlarms,
        actedAndNotRecognized,
      });
    },
    blueText() {
      const summary = this.company.summary || {};
      const {
        actedAndNotRecognized,
        actedAndRecognized,
        notActedAndNotRecognized,
      } = summary.alarms || {};
      if (this.hasActedRecognizedAndNotActedNotRecognizedAlarms(
        actedAndRecognized,
        notActedAndNotRecognized,
      )) {
        return this.$t('alarms-and-warnings.acted-recognized-not-acted-not-recognized', {
          actedAndRecognized,
          notActedAndNotRecognized,
        });
      }
      if (this.areAllActedAlarmsRecognized(actedAndRecognized, actedAndNotRecognized)) {
        return this.$t('alarms-and-warnings.acted-recognized', {
          actedAndRecognized,
        });
      }
      return '';
    },
    lightBlackText() {
      const summary = this.company.summary || {};
      const { notActedAndNotRecognized } = summary.alarms || {};

      if (notActedAndNotRecognized) {
        return this.$t('alarms-and-warnings.not-acted-not-recognized', {
          notActedAndNotRecognized,
        });
      }
      return '';
    },
    lightGreenText() {
      if (this.hasNoActedAlarms()) {
        return this.$t('alarms-and-warnings.no-acted-alarms');
      }
      return '';
    },
  },
  methods: {
    hasActedNotRecognizedAndNotActedNotRecognizedAlarms(
      actedAndNotRecognized,
      notActedAndNotRecognized,
    ) {
      return !!actedAndNotRecognized && !!notActedAndNotRecognized;
    },
    areAllActedAlarmsRecognized(actedAndRecognized, actedAndNotRecognized) {
      return !!actedAndRecognized && !actedAndNotRecognized;
    },
    hasActedRecognizedAndNotActedNotRecognizedAlarms(actedAndRecognized, notActedAndNotRecognized) {
      return !!actedAndRecognized && !!notActedAndNotRecognized;
    },
    hasNoActedAlarms() {
      const summary = this.company.summary || {};
      const {
        actedAndNotRecognized,
        actedAndRecognized,
        notActedAndNotRecognized,
      } = summary.alarms || {};
      return (!actedAndNotRecognized && !actedAndRecognized) || !!notActedAndNotRecognized;
    },
  },
};
</script>

<style lang="sass" scoped>
  .company-alarms-summary
    min-height: 30px
    border-radius: 3px
    padding: 0 8px
    display: flex
    align-items: center
    justify-content: space-between
</style>
